import type {PrimaryConfigOptions} from '@/email-maker/grapesjs-email-editor-plugin/src';

interface KeymapOptions {
  prevent?: boolean;
}

interface Keybinding {
  keys: string;
  handler: string | (() => void);
  isMultiSelectSupported?: boolean;
  opts?: KeymapOptions;
}

/**
 * Listeners related to the keymapper
 * @param opt The grapesjs config object
 */
export default (opt: PrimaryConfigOptions): void => {
  const editor = opt.editor;
  const keymaps = editor.Keymaps;

  const keybindings: Record<string, Keybinding> = {
    'core:undo': {
      keys: '⌘+z, ctrl+z',
      handler: 'core:undo',
    },
    'core:redo': {
      keys: '⌘+shift+z, ctrl+shift+z',
      handler: 'core:redo',
    },
    'component:copy': {
      keys: '⌘+c, ctrl+c',
      handler: window.PMW.emails.commands.CORE_COMPONENT_COPY,
    },
    'component:paste': {
      keys: '⌘+v, ctrl+v',
      handler: window.PMW.emails.commands.CORE_COMPONENT_PASTE,
    },
    'component:cut': {
      keys: '⌘+x, ctrl+x',
      handler: window.PMW.emails.commands.CUT_COMPONENT,
    },
    'component:delete': {
      keys: 'backspace, delete',
      handler: window.PMW.emails.commands.REMOVE_COMPONENT,
    },
    'component:duplicate': {
      keys: '⌘+d, ctrl+d',
      handler: window.PMW.emails.commands.DUPLICATE_COMPONENT,
      opts: {
        prevent: true,
      },
    },
    'component:lock': {
      keys: '⌘+l, ctrl+l',
      handler: window.PMW.emails.commands.TOGGLE_COMPONENT_LOCK,
      opts: {
        prevent: true,
      },
    },
    'component:next-item': {
      keys: 'tab',
      handler: window.PMW.emails.commands.SELECT_NEXT_ITEM,
      opts: {
        prevent: true,
      },
    },
    'component:previous-item': {
      keys: 'shift+tab',
      handler: window.PMW.emails.commands.SELECT_PREVIOUS_ITEM,
      opts: {
        prevent: true,
      },
    },
    'component:unselect': {
      keys: 'esc',
      handler: window.PMW.emails.commands.UNSELECT_COMPONENT,
    },
    'text:align-left': {
      keys: '⌘+shift+l, ctrl+shift+l',
      isMultiSelectSupported: false,
      handler: window.PMW.emails.commands.text.ALIGN_LEFT,
    },
    'text:align-center': {
      keys: '⌘+shift+c, ctrl+shift+c',
      isMultiSelectSupported: false,
      handler: window.PMW.emails.commands.text.ALIGN_CENTER,
      opts: {
        prevent: true,
      },
    },
    'text:align-right': {
      keys: '⌘+shift+r, ctrl+shift+r',
      isMultiSelectSupported: false,
      handler: window.PMW.emails.commands.text.ALIGN_RIGHT,
      opts: {
        prevent: true,
      },
    },
    'text:align-justify': {
      keys: '⌘+shift+j, ctrl+shift+j',
      isMultiSelectSupported: false,
      handler: window.PMW.emails.commands.text.ALIGN_JUSTIFIED,
      opts: {
        prevent: true,
      },
    },
    'text:toggle-bold': {
      keys: '⌘+b, ctrl+b',
      isMultiSelectSupported: false,
      handler: window.PMW.emails.commands.TOGGLE_STYLE_BOLD,
    },
    'text:toggle-italic': {
      keys: '⌘+i, ctrl+i',
      isMultiSelectSupported: false,
      handler: window.PMW.emails.commands.TOGGLE_STYLE_ITALIC,
    },
    'text:toggle-underline': {
      keys: '⌘+u, ctrl+u',
      isMultiSelectSupported: false,
      handler: window.PMW.emails.commands.TOGGLE_STYLE_UNDERLINE,
    },
    'text:toggle-strikethrough': {
      keys: '⌘+s, ctrl+s',
      isMultiSelectSupported: false,
      handler: window.PMW.emails.commands.TOGGLE_STYLE_STRIKETHROUGH,
      opts: {
        prevent: true,
      },
    },
    'core:component-next': {
      keys: 'd',
      handler: 'core:component-next',
    },
    'core:component-prev': {
      keys: 'a',
      handler: 'core:component-prev',
    },
    'core:component-enter': {
      keys: 's',
      handler: 'core:component-enter',
    },
    'core:component-exit': {
      keys: 'w',
      handler: 'core:component-exit',
    },
    'window:zoom-in': {
      keys: '+, =',
      handler: window.PMW.emails.commands.ZOOM_IN,
    },
    'window:zoom-out': {
      keys: '-',
      handler: window.PMW.emails.commands.ZOOM_OUT,
    },
    'window:fit-to-screen': {
      keys: '⌘+0, ctrl+0',
      handler: window.PMW.emails.commands.FIT_TO_SCREEN,
    },
  };

  const getHandlerForMultiSelect = (keybindingCommand: string): (() => void) => {
    return (): void => {
      if (editor.runCommand(window.PMW.emails.commands.IS_MULTISELECT) as boolean) {
        return;
      }

      editor.runCommand(keybindingCommand);
    };
  };

  Object.keys(keybindings).forEach((keymapID: string) => {
    const keybinding = keybindings[keymapID];

    if (keybinding.isMultiSelectSupported === false) {
      keybinding.handler = getHandlerForMultiSelect(keybinding.handler as string);
    }

    keymaps.add(keymapID, keybinding.keys, keybinding.handler, keybinding.opts);
  });
};